import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { Mail, LifeBuoy, Globe, ArrowRight, Check } from 'lucide-react';

/* ----------------------------------------------------------------------------
 * Contact page — neutral base + single signal-green accent, editorial serif
 * headings, hairline-bordered card. Neutral inputs with green focus, green
 * submit. Matches Home.js so marketing + product feel like one system.
 * -------------------------------------------------------------------------- */

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
};

const GREEN = '#2563EB';

const inputClass =
  'block w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30 focus:border-[#2563EB]';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Message sent successfully! We\'ll get back to you within 24 hours.');
    setFormData({
      name: '',
      email: '',
      company: '',
      message: '',
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="bg-[#F8FAFC] dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-white to-[#F8FAFC] dark:from-gray-950 dark:to-gray-950">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-24">
          <motion.div {...reveal} transition={{ duration: 0.5 }} className="mx-auto max-w-3xl text-center">
            <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.05em] text-gray-500">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: GREEN }} />
              Talk to us
            </div>
            <h1 className="mt-6 font-serif text-5xl font-semibold leading-[1.05] tracking-tight text-gray-900 dark:text-white sm:text-6xl">
              Get in touch.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
              Ready to transform your recruitment process? Let's talk about how AutoHire can help your organization.
            </p>
            <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300">
              <Globe size={15} style={{ color: GREEN }} /> Fully remote · global team
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== CONTACT FORM ===== */}
      <section className="mx-auto max-w-4xl px-6 py-20 lg:px-8">
        <motion.div
          {...reveal}
          transition={{ duration: 0.5 }}
          className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_48px_-24px_rgba(15,23,42,0.18)] sm:p-10"
        >
          <div className="text-center">
            <div className="text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: GREEN }}>
              Send us a message
            </div>
            <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">
              We'll respond within 24 hours.
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="mt-10 space-y-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label htmlFor="name" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Full name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="you@company.com"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="company" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Company name *
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className={inputClass}
                placeholder="Acme Inc."
                required
              />
            </div>

            <div>
              <label htmlFor="message" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={6}
                className={inputClass}
                placeholder="Tell us about your hiring needs..."
                required
              />
            </div>

            <button
              type="submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3.5 text-base font-semibold text-white transition-colors"
              style={{ background: GREEN }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#1D4FD8')}
              onMouseLeave={(e) => (e.currentTarget.style.background = GREEN)}
            >
              Send message <ArrowRight size={17} />
            </button>
          </form>

          <div className="mt-10 grid grid-cols-1 gap-4 border-t border-gray-200 dark:border-gray-800 pt-8 sm:grid-cols-2">
            <a
              href="mailto:sales@autohire.tech"
              className="group flex items-start gap-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 transition-colors hover:border-gray-300 dark:hover:border-gray-700"
            >
              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl" style={{ background: '#EFF4FE', color: GREEN }}>
                <Mail size={18} strokeWidth={1.8} />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-gray-900 dark:text-white">Enterprise sales</span>
                <span className="block truncate text-sm text-gray-500 dark:text-gray-400">sales@autohire.tech</span>
              </span>
            </a>
            <a
              href="mailto:support@autohire.tech"
              className="group flex items-start gap-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 transition-colors hover:border-gray-300 dark:hover:border-gray-700"
            >
              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl" style={{ background: '#EFF4FE', color: GREEN }}>
                <LifeBuoy size={18} strokeWidth={1.8} />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-gray-900 dark:text-white">Support</span>
                <span className="block truncate text-sm text-gray-500 dark:text-gray-400">support@autohire.tech</span>
              </span>
            </a>
          </div>

          <p className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Check size={15} style={{ color: GREEN }} /> Fully remote company · available worldwide
          </p>
        </motion.div>
      </section>
    </div>
  );
}

export default Contact;
